import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DEFAULT_TEMPLATES = [
  {
    id: 'tpl-1',
    name: 'Critical & High Priority',
    description: 'All critical and high priority tickets',
    filters: { search: '', status: 'all', priority: 'high', technician: 'all', location: 'all', dateFrom: '', dateTo: '' },
    isDefault: true
  },
  {
    id: 'tpl-2',
    name: 'In Progress Tickets',
    description: 'All currently active tickets',
    filters: { search: '', status: 'in-progress', priority: 'all', technician: 'all', location: 'all', dateFrom: '', dateTo: '' },
    isDefault: true
  },
  {
    id: 'tpl-3',
    name: 'Pending Review',
    description: 'Tickets awaiting action',
    filters: { search: '', status: 'pending', priority: 'all', technician: 'all', location: 'all', dateFrom: '', dateTo: '' },
    isDefault: true
  }
];

const STORAGE_KEY = 'ticket_filter_templates';

const SavedFilterTemplates = ({ currentFilters, onLoadTemplate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [savedTemplates, setSavedTemplates] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const allTemplates = [...DEFAULT_TEMPLATES, ...savedTemplates];

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSaveTemplate = () => {
    if (!templateName?.trim()) return;
    const newTemplate = {
      id: `tpl-${Date.now()}`,
      name: templateName?.trim(),
      description: templateDesc?.trim(),
      filters: { ...currentFilters },
      isDefault: false,
      createdAt: new Date()?.toISOString()
    };
    const updated = [...savedTemplates, newTemplate];
    setSavedTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setTemplateName('');
    setTemplateDesc('');
    setIsSaving(false);
    showSuccess(`Template "${newTemplate?.name}" saved!`);
  };

  const handleDeleteTemplate = (id) => {
    const updated = savedTemplates?.filter(t => t?.id !== id);
    setSavedTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setDeleteConfirm(null);
    showSuccess('Template deleted.');
  };

  const handleLoadTemplate = (template) => {
    onLoadTemplate?.(template?.filters);
    setIsOpen(false);
    showSuccess(`Loaded "${template?.name}"`);
  };

  const getActiveFilterCount = (filters) => {
    let count = 0;
    if (filters?.search) count++;
    if (filters?.status && filters?.status !== 'all') count++;
    if (filters?.priority && filters?.priority !== 'all') count++;
    if (filters?.technician && filters?.technician !== 'all') count++;
    if (filters?.location && filters?.location !== 'all') count++;
    if (filters?.dateFrom) count++;
    if (filters?.dateTo) count++;
    return count;
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(prev => !prev)}
        iconName="Bookmark"
        iconPosition="left"
      >
        Filter Templates
        {savedTemplates?.length > 0 && (
          <span className="ml-1.5 px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
            {savedTemplates?.length}
          </span>
        )}
      </Button>
      {/* Success Toast */}
      {successMsg && (
        <div className="absolute top-10 right-0 z-50 bg-success/10 border border-success/20 text-success text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap flex items-center gap-1.5">
          <Icon name="CheckCircle" size={12} color="var(--color-success)" />
          {successMsg}
        </div>
      )}
      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setIsOpen(false); setIsSaving(false); }}
          />
          <div className="absolute right-0 top-10 z-50 w-80 bg-card border border-border rounded-xl shadow-elevation-3 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Icon name="Bookmark" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-foreground">Filter Templates</span>
              </div>
              <button
                onClick={() => { setIsOpen(false); setIsSaving(false); }}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>

            {/* Save Current Filters */}
            <div className="px-4 py-3 border-b border-border">
              {!isSaving ? (
                <button
                  onClick={() => setIsSaving(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors text-sm text-muted-foreground hover:text-primary"
                >
                  <Icon name="Plus" size={14} color="currentColor" />
                  Save current filters as template
                </button>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Template name *"
                    value={templateName}
                    onChange={(e) => setTemplateName(e?.target?.value)}
                    className="text-sm"
                    autoFocus
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={templateDesc}
                    onChange={(e) => setTemplateDesc(e?.target?.value)}
                    className="text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveTemplate}
                      disabled={!templateName?.trim()}
                      className="flex-1"
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setIsSaving(false); setTemplateName(''); setTemplateDesc(''); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Templates List */}
            <div className="max-h-72 overflow-y-auto">
              {allTemplates?.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Icon name="BookmarkX" size={28} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm text-muted-foreground">No templates yet</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {/* Default Templates */}
                  <p className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wide">Quick Filters</p>
                  {DEFAULT_TEMPLATES?.map(template => {
                    let count = getActiveFilterCount(template?.filters);
                    return (
                      <div
                        key={template?.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/60 group transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-foreground truncate">{template?.name}</span>
                            {count > 0 && (
                              <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full flex-shrink-0">{count}</span>
                            )}
                          </div>
                          {template?.description && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">{template?.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleLoadTemplate(template)}
                          className="flex-shrink-0 px-2 py-1 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        >
                          Apply
                        </button>
                      </div>
                    );
                  })}

                  {/* User Templates */}
                  {savedTemplates?.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wide mt-2">My Templates</p>
                      {savedTemplates?.map(template => {
                        let count = getActiveFilterCount(template?.filters);
                        return (
                          <div
                            key={template?.id}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/60 group transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-foreground truncate">{template?.name}</span>
                                {count > 0 && (
                                  <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full flex-shrink-0">{count}</span>
                                )}
                              </div>
                              {template?.description && (
                                <p className="text-xs text-muted-foreground truncate mt-0.5">{template?.description}</p>
                              )}
                              {template?.createdAt && (
                                <p className="text-xs text-muted-foreground/60 mt-0.5">
                                  {new Date(template.createdAt)?.toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleLoadTemplate(template)}
                                className="px-2 py-1 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors"
                              >
                                Apply
                              </button>
                              {deleteConfirm === template?.id ? (
                                <>
                                  <button
                                    onClick={() => handleDeleteTemplate(template?.id)}
                                    className="px-2 py-1 text-xs font-medium text-error bg-error/10 hover:bg-error/20 rounded-md transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="p-1 rounded hover:bg-muted transition-colors"
                                  >
                                    <Icon name="X" size={12} color="var(--color-muted-foreground)" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(template?.id)}
                                  className="p-1 rounded hover:bg-error/10 transition-colors"
                                  title="Delete template"
                                >
                                  <Icon name="Trash2" size={13} color="var(--color-muted-foreground)" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SavedFilterTemplates;
